import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  CircularProgress, 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  Box, 
  Grid, 
  Divider, 
  Chip, 
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText
} from '@mui/material';
import './Stallion.css';

// Add custom styles for Chinese origin items
const styles = {
  chineseOriginItem: {
    backgroundColor: 'rgba(255, 200, 200, 0.3)'
  },
  originWarning: {
    color: '#d32f2f',
    fontWeight: 'bold'
  },
  warningMessage: {
    color: '#d32f2f',
    padding: '8px',
    marginBottom: '12px',
    border: '1px solid #d32f2f',
    borderRadius: '4px',
    backgroundColor: 'rgba(255, 200, 200, 0.2)'
  }
};

const API_URL = import.meta.env.VITE_API_URL || '';

const Stallion = () => {
  // State for error handling
  const [error, setError] = useState(null);
  
  // State for shipment scanning
  const [shipmentCode, setShipmentCode] = useState('');
  const [shipmentData, setShipmentData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // State for credits
  const [credits, setCredits] = useState(null);
  const [creditsNeeded, setCreditsNeeded] = useState(0);
  const [creditsToAdd, setCreditsToAdd] = useState(0);
  const [isAddingCredits, setIsAddingCredits] = useState(false);
  const [creditsError, setCreditsError] = useState(null);
  
  // State for remanifesting
  const [isRemanifesting, setIsRemanifesting] = useState(false);
  const [remanifestSuccess, setRemanifestSuccess] = useState(false);
  const [newShipmentData, setNewShipmentData] = useState(null);
  
  // State for China items management
  const [itemsToRemove, setItemsToRemove] = useState([]);
  const [showChineseItemsWarning, setShowChineseItemsWarning] = useState(false);
  
  // State for cache loading
  const [cacheStatus, setCacheStatus] = useState(null);
  const [isCacheLoading, setIsCacheLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [showLoadingDialog, setShowLoadingDialog] = useState(false);
  const [allShipments, setAllShipments] = useState([]);
  const [cacheError, setCacheError] = useState(null);
  
  // Ref for input focus
  const shipmentInputRef = useRef(null);

  // Load data on component mount
  useEffect(() => {
    // Check cache status first
    checkCacheStatus();
    fetchCredits();
    
    // Check for saved remanifest label in localStorage
    const savedLabel = localStorage.getItem('remanifestLabel');
    if (savedLabel) {
      try {
        const parsedLabel = JSON.parse(savedLabel);
        setNewShipmentData(parsedLabel);
      } catch (err) {
        console.error('Error parsing saved label:', err);
        localStorage.removeItem('remanifestLabel');
      }
    }
    
    // Focus on shipment code input
    setTimeout(() => {
      if (shipmentInputRef.current) {
        shipmentInputRef.current.focus();
      }
    }, 100);
    
    // Set up polling for cache status if it's updating
    const statusInterval = setInterval(() => {
      if (cacheStatus && cacheStatus.updating) {
        checkCacheStatus();
      }
    }, 5000); // Check every 5 seconds
    
    return () => clearInterval(statusInterval);
  }, []);
  
  // Monitor cache status changes
  useEffect(() => {
    if (cacheStatus) {
      // If cache is updating, show loading dialog
      if (cacheStatus.updating) {
        setShowLoadingDialog(true);
      } else if (cacheStatus.shipmentCount > 0) {
        // If cache is ready, hide dialog and fetch shipments
        setShowLoadingDialog(false);
        fetchAllShipments();
      }
    }
  }, [cacheStatus]);
  
  // Recalculate credits needed when items are selected/deselected for removal
  useEffect(() => {
    if (shipmentData && hasChineseOrigin(shipmentData)) {
      calculateCreditsNeeded(shipmentData);
    }
  }, [itemsToRemove]);
  
  // Check the status of the cache
  const checkCacheStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stallion-cache-status`);
      setCacheStatus(response.data);
      
      // Update error state if there's an error in the cache
      if (response.data.error) {
        setCacheError(response.data.error);
      } else {
        setCacheError(null);
      }
      
      // If cache is empty or not initialized, trigger an update
      if (!response.data.lastUpdated || response.data.shipmentCount === 0) {
        refreshCache();
      } else {
        setIsCacheLoading(false);
      }
      
      // Update loading progress based on cache status
      if (response.data.progress && response.data.updating) {
        const { clientsCompleted, totalClients } = response.data.progress;
        if (totalClients > 0) {
          setLoadingProgress((clientsCompleted / totalClients) * 100);
        }
      }
    } catch (err) {
      console.error('Error checking cache status:', err);
      setCacheError(err.message || 'Failed to check cache status');
      setIsCacheLoading(false);
    }
  };
  
  // Fetch all shipments from cache
  const fetchAllShipments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stallion-all-shipments`);
      
      if (response.data.status === 'updating') {
        // Cache is still updating
        setCacheStatus({
          ...cacheStatus,
          updating: true,
          progress: response.data.progress
        });
        return;
      }
      
      // Check for errors in the response
      if (response.data.error) {
        setCacheError(response.data.error);
        console.error('Error in cache data:', response.data.error);
      } else {
        setCacheError(null);
      }
      
      setAllShipments(response.data.data || []);
      setIsCacheLoading(false);
      setShowLoadingDialog(false);
      
      // Log cache statistics
      console.log(`Loaded ${response.data.data?.length || 0} shipments from cache`);
      console.log(`Cache age: ${response.data.cacheAge || 'unknown'} minutes`);
    } catch (err) {
      console.error('Error fetching all shipments:', err);
      setCacheError(err.message || 'Failed to fetch shipments');
      setIsCacheLoading(false);
      setShowLoadingDialog(false);
    }
  };
  
  // Force refresh the cache - clear and reload all data
  const refreshCache = async () => {
    setShowLoadingDialog(true);
    setIsCacheLoading(true);
    setAllShipments([]); // Clear local shipments data
    setShipmentData(null); // Clear current shipment display
    setCacheError(null); // Clear any previous cache errors
    setLoadingProgress(0); // Reset progress
    
    try {
      // This endpoint will clear the cache and start a fresh reload
      const response = await axios.post(`${API_URL}/api/stallion-refresh-cache`);
      
      if (response.data.inProgress) {
        // Update is already in progress
        setCacheStatus({
          ...cacheStatus,
          updating: true,
          progress: response.data.progress,
          shipmentCount: 0 // Reset count since we're reloading
        });
        
        // Start polling for updates
        const pollInterval = setInterval(async () => {
          await checkCacheStatus();
          // Stop polling once update is complete
          if (cacheStatus && !cacheStatus.updating) {
            clearInterval(pollInterval);
          }
        }, 3000); // Check every 3 seconds
        
        // Safety cleanup after 5 minutes (in case something goes wrong)
        setTimeout(() => clearInterval(pollInterval), 5 * 60 * 1000);
      } else if (response.data.error) {
        setCacheError(response.data.error);
        setError(`Cache refresh error: ${response.data.error}`);
        setShowLoadingDialog(false);
        setIsCacheLoading(false);
      } else {
        // Update completed successfully
        setCacheStatus({
          lastUpdated: response.data.lastUpdated,
          updating: false,
          shipmentCount: response.data.count,
          progress: { message: 'Cache update completed' }
        });
        setLoadingProgress(100);
        fetchAllShipments();
      }
    } catch (err) {
      console.error('Error refreshing cache:', err);
      setCacheError(err.message || 'Failed to refresh shipment data');
      setError('Failed to refresh shipment data');
      setShowLoadingDialog(false);
      setIsCacheLoading(false);
    }
  };

  // Fetch account credits using the client's API key
  const fetchCredits = async (apiKey) => {
    if (!apiKey) {
      console.log('No API key provided for fetching credits');
      return;
    }
    
    setCreditsError(null);
    
    try {
      console.log(`Fetching credits with API key: ${apiKey.substring(0, 10)}...`);
      
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      
      const response = await axios.get(`${API_URL}/api/stallion-credits`, {
        params: { apiKey, _t: timestamp },
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      console.log('Credits response:', response.data);
      
      // Extract the available credits value
      let availableValue = '0.00';
      
      if (response.data && response.data.available) {
        availableValue = response.data.available;
        console.log(`Found available credits in response: ${availableValue}`);
      } else if (response.data && response.data.credits) {
        availableValue = response.data.credits;
        console.log(`Found credits in response: ${availableValue}`);
      }
      
      // Ensure we have a properly formatted credits object
      const creditsData = {
        available: parseFloat(availableValue || 0).toFixed(2)
      };
      
      console.log('Formatted credits data:', creditsData);
      setCredits(creditsData);
      
      // If we have credits data and credits needed, calculate how many to add
      if (creditsData && creditsNeeded) {
        const availableCredits = parseFloat(creditsData.available || 0);
        const needed = parseFloat(creditsNeeded);
        
        // Determine the minimum purchase amount (either $10 or the total credits required, whichever is higher)
        const minPurchaseAmount = Math.max(10, needed);
        
        if (availableCredits < needed) {
          // Need to add more credits - calculate the exact amount needed
          const amountNeeded = Math.ceil(needed - availableCredits);
          console.log(`Credits needed: ${needed}, Available: ${availableCredits}, Amount needed: ${amountNeeded}, Min purchase: ${minPurchaseAmount}`);
          setCreditsToAdd(Math.max(amountNeeded, 10));
        } else {
          // Have enough credits - set to minimum purchase amount
          setCreditsToAdd(minPurchaseAmount);
        }
      } else {
        // Default to minimum $10 if we don't have complete information
        setCreditsToAdd(10);
      }
    } catch (err) {
      console.error('Error fetching credits:', err);
      setCreditsError('Failed to fetch credits');
      setCredits(null);
      // Set default minimum amount even on error
      setCreditsToAdd(10);
    }
  };

  // Handle shipment code submission
  const handleShipmentSubmit = async (e) => {
    e.preventDefault();
    if (!shipmentCode.trim()) return;
    
    console.log(`Searching for shipment code: ${shipmentCode}`);
    
    setIsLoading(true);
    setShipmentData(null);
    setError(null);
    setRemanifestSuccess(false);
    setItemsToRemove([]);
    setShowChineseItemsWarning(false);
    
    // First check if the shipment is in our local cache
    if (allShipments.length > 0) {
      const cachedShipment = allShipments.find(s => s.ship_code === shipmentCode);
      
      if (cachedShipment) {
        console.log('Found shipment in local cache:');
        console.log(JSON.stringify(cachedShipment, null, 2));
        
        // Extract country of origin from cached shipment items
        if (cachedShipment.items && 
            Array.isArray(cachedShipment.items) && 
            cachedShipment.items.length > 0 && 
            cachedShipment.items[0] && 
            cachedShipment.items[0].country_of_origin) {
          
          cachedShipment.countryOfOrigin = cachedShipment.items[0].country_of_origin;
          console.log('Extracted country of origin from cached shipment:', cachedShipment.countryOfOrigin);
        }
        
        setShipmentData(cachedShipment);
        calculateCreditsNeeded(cachedShipment);
        
        // Fetch credits using the client's API key
        if (cachedShipment.clientApiKey) {
          console.log('Fetching credits for cached shipment');
          fetchCredits(cachedShipment.clientApiKey);
        }
        
        setIsLoading(false);
        return;
      }
    }
    
    console.log('Shipment not found in cache, fetching from API...');
    
    // If not in local cache, fetch from API
    try {
      const response = await axios.get(`${API_URL}/api/stallion-shipment/${shipmentCode}`);
      
      console.log('API response for shipment:', shipmentCode);
      console.log(JSON.stringify(response.data, null, 2));
      
      // Debug country of origin
      if (response.data && response.data.items && response.data.items.length > 0) {
        console.log('Country of Origin from API:', response.data.items[0].country_of_origin);
        
        // Ensure the country of origin is directly accessible in the shipment data
        if (response.data.items[0].country_of_origin) {
          response.data.countryOfOrigin = response.data.items[0].country_of_origin;
          console.log('Added countryOfOrigin property:', response.data.countryOfOrigin);
        }
      } else {
        console.log('No items or country of origin found in API response');
      }
      
      setShipmentData(response.data);
      
      // Calculate credits needed based on shipment data
      calculateCreditsNeeded(response.data);
      
      // Fetch credits using the client's API key
      if (response.data.clientApiKey) {
        console.log('Fetching credits for API response shipment');
        fetchCredits(response.data.clientApiKey);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch shipment data');
      setShipmentData(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate credits needed based on shipment data
  const calculateCreditsNeeded = (data) => {
    if (!data) {
      setCreditsNeeded(0);
      return;
    }
    
    // Create a modified data object if there are Chinese items to be removed
    let effectiveData = data;
    
    if (hasChineseOrigin(data) && itemsToRemove.length > 0 && data.items) {
      // Get the items that won't be removed
      const remainingItems = data.items.filter((_, index) => !itemsToRemove.includes(index));
      
      // Calculate the new total weight and value after removing Chinese items
      let newTotalWeight = 0;
      let newTotalValue = 0;
      
      remainingItems.forEach(item => {
        // Add item weight to total if available
        if (item.weight) {
          newTotalWeight += parseFloat(item.weight) * (item.quantity || 1);
        }
        
        // Add item value to total
        if (item.value) {
          newTotalValue += parseFloat(item.value) * (item.quantity || 1);
        }
      });
      
      // If we can't calculate new weight from items, estimate based on percentage of items removed
      if (newTotalWeight === 0 && data.weight) {
        const percentRemaining = remainingItems.length / data.items.length;
        newTotalWeight = parseFloat(data.weight) * percentRemaining;
      }
      
      // Create the effective data with adjusted weight and items
      effectiveData = {
        ...data,
        weight: newTotalWeight > 0 ? newTotalWeight.toString() : data.weight,
        value: newTotalValue > 0 ? newTotalValue.toString() : data.value,
        items: remainingItems
      };
      
      console.log('Adjusted weight after removing Chinese items:', effectiveData.weight);
    }
    
    // Use the rate directly from the shipment data
    if (effectiveData.rate) {
      console.log(`Using rate from shipment data: ${effectiveData.rate}`);
      setCreditsNeeded(effectiveData.rate);
      return;
    }
    
    // Fallback calculation if rate is not available
    console.log('No rate found in shipment data, using fallback calculation');
    const baseCredits = 1;
    let additionalCredits = 0;
    
    if (effectiveData.weight) {
      const weight = parseFloat(effectiveData.weight);
      if (weight > 5) {
        additionalCredits += Math.ceil(weight / 5);
      }
    }
    
    setCreditsNeeded(baseCredits + additionalCredits);
  };

  // Handle toggling items for removal
  const handleToggleItemRemoval = (index) => {
    setItemsToRemove(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
    // Note: No need to call calculateCreditsNeeded here as it will be triggered by the useEffect
  };

  // Handle remanifest button click
  const handleRemanifest = async () => {
    setIsRemanifesting(true);
    setError(null);
    setRemanifestSuccess(false);
    setNewShipmentData(null);
    
    try {
      if (!shipmentData || !shipmentData.clientApiKey) {
        throw new Error('Missing shipment data or API key');
      }
      
      // Create a modified shipment data with selected China items removed if necessary
      let modifiedShipmentData = { ...shipmentData };
      
      // Filter out items selected for removal if there are any
      if (hasChineseOrigin(shipmentData) && itemsToRemove.length > 0) {
        // Create a new array with only the items not selected for removal
        const filteredItems = shipmentData.items.filter((_, index) => !itemsToRemove.includes(index));
        
        // Update the modified shipment data
        modifiedShipmentData = {
          ...shipmentData,
          items: filteredItems
        };
        
        console.log(`Remanifesting without ${itemsToRemove.length} China-made items`);
      }
      
      // Step 1: Remanifest the shipment
      console.log('Remanifesting shipment:', shipmentCode);
      const remanifestResponse = await axios.post(`${API_URL}/api/stallion-remanifest`, {
        shipmentCode,
        shipmentData: modifiedShipmentData,
        apiKey: shipmentData.clientApiKey,
        removedChineseItems: itemsToRemove.length > 0
      });
      
      if (!remanifestResponse.data.success) {
        throw new Error(remanifestResponse.data.error || 'Failed to remanifest shipment');
      }
      
      console.log('Remanifest successful:', remanifestResponse.data);
      
      // Save the new shipment data and label to localStorage
      const newShipmentInfo = {
        shipmentCode: remanifestResponse.data.newShipmentCode,
        trackingCode: remanifestResponse.data.trackingCode,
        labelUrl: remanifestResponse.data.labelUrl,
        labelBase64: remanifestResponse.data.labelBase64,
        timestamp: new Date().toISOString()
      };
      
      localStorage.setItem('remanifestLabel', JSON.stringify(newShipmentInfo));
      setNewShipmentData(newShipmentInfo);
      
      // Step 2: Void the old shipment
      await handleVoidLabel(true);
      
      // Step 3: Show success message
      setRemanifestSuccess(true);
      
      // Refresh credits after remanifesting
      fetchCredits();
    } catch (err) {
      console.error('Remanifest error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to remanifest shipment');
    } finally {
      setIsRemanifesting(false);
    }
  };

  // Function to download the remanifested label
  const downloadRemanifestLabel = () => {
    if (!newShipmentData || !newShipmentData.labelBase64 || !shipmentCode) return;
    
    // Create a blob from the base64 data
    const byteCharacters = atob(newShipmentData.labelBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    
    // Create a link and trigger download
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    // Name the file as "Replace XYZ" where XYZ is the original shipment code
    link.download = `Replace ${shipmentCode}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle adding credits
  const handleAddCredits = async (amount) => {
    if (!shipmentData || !shipmentData.clientApiKey) {
      setCreditsError('No client API key available');
      return;
    }
    
    // Ensure minimum purchase amount is $10
    const purchaseAmount = parseFloat(amount);
    if (isNaN(purchaseAmount) || purchaseAmount < 10) {
      setCreditsError('Minimum purchase amount is $10');
      return;
    }
    
    setIsAddingCredits(true);
    setCreditsError(null);
    
    try {
      console.log(`Adding ${purchaseAmount} credits with API key: ${shipmentData.clientApiKey.substring(0, 10)}...`);
      
      // Make sure we're using the client's API key from the scanned shipment
      if (!shipmentData.clientApiKey) {
        throw new Error('No client API key available for this shipment');
      }
      
      const response = await axios.post(`${API_URL}/api/stallion-add-credits`, {
        apiKey: shipmentData.clientApiKey,
        amount: purchaseAmount
      });
      
      console.log('Add credits response:', response.data);
      
      // The backend now returns both the purchase response and the latest credits
      if (response.data && response.data.success) {
        console.log('Credits added successfully');
        
        // Check if we have the latest credits in the response
        if (response.data.credits && response.data.credits.available) {
          console.log('Using latest credits from response:', response.data.credits.available);
          
          // Update the credits state with the latest amount
          const updatedCredits = {
            available: response.data.credits.available
          };
          
          setCredits(updatedCredits);
          console.log('Updated credits from response:', updatedCredits);
        } else if (response.data.purchase) {
          // Fallback to the purchase response if we don't have the latest credits
          console.log('Purchase successful, but using purchase response for credits');
          
          // Refresh credits to get the latest amount
          await fetchCredits(shipmentData.clientApiKey);
        }
        
        // Get the latest credits value for calculations
        const latestCredits = credits ? credits : { available: '0.00' };
        
        // Calculate new credits to add based on updated balance
        const availableCredits = parseFloat(latestCredits.available || 0);
        const needed = parseFloat(creditsNeeded);
        
        if (availableCredits < needed) {
          // Still need more credits
          const amountNeeded = Math.ceil(needed - availableCredits);
          setCreditsToAdd(Math.max(amountNeeded, 10));
        } else {
          // Have enough credits now
          setCreditsToAdd(Math.max(10, needed));
        }
      } else {
        // Handle unsuccessful response
        setCreditsError(response.data?.message || 'Failed to add credits');
      }
    } catch (err) {
      console.error('Error adding credits:', err);
      setCreditsError(err.response?.data?.error || 'Failed to add credits. Please try again.');
    } finally {
      setIsAddingCredits(false);
    }
  };

  // Handle void label
  const handleVoidLabel = async (skipError = false) => {
    if (!shipmentData || !shipmentData.clientApiKey) return;
    
    try {
      console.log('Voiding shipment:', shipmentCode);
      const response = await axios.post(`${API_URL}/api/stallion-void-label`, {
        shipmentCode,
        apiKey: shipmentData.clientApiKey
      });
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to void shipment');
      }
      
      console.log('Void successful:', response.data);
      
      // Update shipment data to reflect voided status
      setShipmentData({
        ...shipmentData,
        status: 'voided'
      });
      
      // Refresh credits after voiding
      fetchCredits();
    } catch (err) {
      console.error('Void error:', err);
      if (!skipError) {
        setError(err.response?.data?.error || err.message || 'Failed to void label');
      } else {
        console.error('Error voiding label after remanifest:', err);
      }
    }
  };

  // Clear current shipment and reset form
  const handleClear = () => {
    setShipmentCode('');
    setShipmentData(null);
    setCreditsNeeded(0);
    setError(null);
    setRemanifestSuccess(false);
    setNewShipmentData(null);
    setItemsToRemove([]);
    setShowChineseItemsWarning(false);
    
    if (shipmentInputRef.current) {
      shipmentInputRef.current.focus();
    }
  };

  // Format date for display in a more readable format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    
    // Format as "May 2nd, 2025 at 4:32 PM"
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    
    // Get the day with ordinal suffix (1st, 2nd, 3rd, etc.)
    const day = date.getDate();
    const suffix = getDaySuffix(day);
    
    // Format the date using Intl.DateTimeFormat
    let formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);
    
    // Replace the day number with day + suffix
    formattedDate = formattedDate.replace(`, ${day},`, ` ${day}${suffix},`);
    
    // Replace the comma before time with 'at'
    formattedDate = formattedDate.replace(', ', ' at ');
    
    return formattedDate;
  };
  
  // Helper function to get day suffix (1st, 2nd, 3rd, etc.)
  const getDaySuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };
  
  // Helper function to extract country of origin from shipment data
  const getCountryOfOrigin = (data) => {
    console.log('Extracting country of origin from:', data);
    
    // First check the direct property
    if (data.countryOfOrigin) {
      console.log('Found direct countryOfOrigin property:', data.countryOfOrigin);
      return data.countryOfOrigin;
    }
    
    // Then check the items array
    if (data.items && 
        Array.isArray(data.items) && 
        data.items.length > 0 && 
        data.items[0]) {
      
      console.log('Checking items[0]:', data.items[0]);
      
      if (data.items[0].country_of_origin) {
        console.log('Found country_of_origin in items:', data.items[0].country_of_origin);
        return data.items[0].country_of_origin;
      }
    }
    
    console.log('No country of origin found');
    return 'N/A';
  };

  // Helper function to check if any item has China origin
  const hasChineseOrigin = (data) => {
    if (!data || !data.items || !Array.isArray(data.items)) return false;
    
    return data.items.some(item => {
      const origin = item.country_of_origin || item.origin;
      return origin === 'CN' || origin === 'China';
    });
  };

  // Helper function to check if shipment was created after May 2nd, 2025
  const isCreatedAfterCutoffDate = (data) => {
    if (!data || !data.created_at) return false;
    
    const cutoffDate = new Date('2025-05-02T00:00:00Z');
    const createdDate = new Date(data.created_at);
    
    return createdDate > cutoffDate;
  };

  // Format currency for display
  const formatCurrency = (value, currency = 'USD') => {
    if (!value) return 'N/A';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(value);
  };

  return (
    <div className="stallion-container">
      {/* Loading Dialog */}
      <Dialog open={showLoadingDialog} disableEscapeKeyDown={true} disableBackdropClick={true}>
        <DialogTitle>Loading Shipments</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Loading shipments from all clients. This may take a few minutes...
          </DialogContentText>
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress variant="determinate" value={loadingProgress} />
          </Box>
          {cacheStatus && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2">
                {cacheStatus.updating ? 'Updating cache...' : 'Cache ready'}
              </Typography>
              {cacheStatus.progress && cacheStatus.progress.message && (
                <Typography variant="body2" color="primary">
                  {cacheStatus.progress.message}
                </Typography>
              )}
              {cacheStatus.shipmentCount > 0 && (
                <Typography variant="body2">
                  {cacheStatus.shipmentCount} shipments loaded
                </Typography>
              )}
              {cacheStatus.progress && cacheStatus.progress.clientsCompleted > 0 && (
                <Typography variant="body2">
                  Completed {cacheStatus.progress.clientsCompleted} of {cacheStatus.progress.totalClients} clients
                </Typography>
              )}
            </Box>
          )}
          {cacheError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {cacheError}
            </Alert>
          )}
        </DialogContent>
      </Dialog>
      
      <div className="stallion-dashboard">
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Stallion Shipment Management
            </Typography>
            
            {/* Cache status indicator */}
            {cacheStatus && cacheStatus.lastUpdated && (
              <Typography variant="caption" display="block" sx={{ mb: 2 }}>
                Last updated: {new Date(cacheStatus.lastUpdated).toLocaleString()}
                {cacheStatus.cacheAge !== null && (
                  <span> ({cacheStatus.cacheAge} minutes ago)</span>
                )}
                <Button 
                  size="small" 
                  variant="text" 
                  onClick={refreshCache} 
                  disabled={isCacheLoading}
                  sx={{ ml: 1 }}
                >
                  Refresh
                </Button>
              </Typography>
            )}
            
            {/* Cache error display */}
            {cacheError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Cache Error: {cacheError}
                <Button 
                  size="small" 
                  variant="contained" 
                  color="error"
                  onClick={refreshCache} 
                  sx={{ ml: 2 }}
                >
                  Retry
                </Button>
              </Alert>
            )}
            
            {/* Credits Display - Only show after a shipment is scanned */}
            {shipmentData && (
              <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6">
                      Available Credits: {credits ? (credits.available !== undefined ? credits.available : 0) : 'Loading...'}
                    </Typography>
                    
                    {/* Credits needed for remanifest */}
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        mt: 1, 
                        fontWeight: 'bold',
                        color: credits && parseFloat(credits.available || 0) >= parseFloat(creditsNeeded) ? 'green' : 'red'
                      }}
                    >
                      Credits Required: {creditsNeeded}
                      {credits && ` (${parseFloat(credits.available || 0) >= parseFloat(creditsNeeded) ? 'Sufficient' : 'Insufficient'})`}
                    </Typography>
                    
                    {creditsError && (
                      <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                        {creditsError}
                      </Alert>
                    )}
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {/* Show credit status message */}
                      {credits && (
                        <Typography 
                          variant="body2" 
                          color={parseFloat(credits.available || 0) >= parseFloat(creditsNeeded) ? 'success.main' : 'error'}
                          sx={{ fontWeight: 'bold', mb: 1 }}
                        >
                          {parseFloat(credits.available || 0) >= parseFloat(creditsNeeded) 
                            ? 'You have enough credits for remanifest' 
                            : `You need ${Math.max(0, parseFloat(creditsNeeded) - parseFloat(credits.available || 0)).toFixed(2)} more credits`}
                        </Typography>
                      )}
                      
                      {/* Credit purchase input */}
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          label="Amount to Purchase"
                          type="number"
                          size="small"
                          value={creditsToAdd}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) || 0;
                            setCreditsToAdd(Math.max(0, value));
                          }}
                          InputProps={{ 
                            inputProps: { min: 10, step: 1 },
                            startAdornment: <span style={{ marginRight: 4 }}>$</span>
                          }}
                          sx={{ width: '150px' }}
                          helperText={parseFloat(creditsToAdd) < 10 ? 'Minimum $10' : ''}
                          error={parseFloat(creditsToAdd) < 10}
                        />
                        
                        <Button 
                          variant="contained" 
                          color="primary"
                          onClick={() => handleAddCredits(creditsToAdd)}
                          disabled={isAddingCredits || parseFloat(creditsToAdd) < 10}
                        >
                          {isAddingCredits ? <CircularProgress size={24} /> : 'Add Credits'}
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}
            
            {/* Shipment Scanning Form */}
            <Paper elevation={3} sx={{ p: 3 }}>
              <form onSubmit={handleShipmentSubmit}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={9}>
                    <TextField
                      inputRef={shipmentInputRef}
                      label="Scan Stallion Shipment Code"
                      value={shipmentCode}
                      onChange={(e) => setShipmentCode(e.target.value)}
                      fullWidth
                      autoFocus
                      placeholder="Scan or enter shipment code"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button 
                      type="submit" 
                      variant="contained" 
                      color="primary" 
                      fullWidth
                      disabled={isLoading}
                    >
                      {isLoading ? <CircularProgress size={24} /> : 'Lookup'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {remanifestSuccess && (
            <Alert severity="success" sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>Shipment successfully remanifested and original label voided!</Typography>
                {newShipmentData && (
                  <Button 
                    variant="contained" 
                    color="success" 
                    size="small"
                    onClick={downloadRemanifestLabel}
                  >
                    Download Label
                  </Button>
                )}
              </Box>
            </Alert>
          )}
          
          {/* Saved remanifest label */}
          {!remanifestSuccess && newShipmentData && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>
                  You have a previously remanifested label (Shipment: {newShipmentData.shipmentCode}).
                </Typography>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  size="small"
                  onClick={downloadRemanifestLabel}
                >
                  Download Label
                </Button>
              </Box>
            </Alert>
          )}
          
          {/* Shipment Details */}
          {shipmentData && (
            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
              {/* Quality Assurance Warnings */}
              {hasChineseOrigin(shipmentData) && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    WARNING: This shipment contains items made in China and cannot be shipped!
                  </Typography>
                </Alert>
              )}
              
              {isCreatedAfterCutoffDate(shipmentData) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    This shipment was created after May 2nd, 2025. It should be fine to ship without remanifesting.
                  </Typography>
                </Alert>
              )}
              
              {/* Client Name in H1 */}
              {shipmentData.client && (
                <Typography variant="h1" sx={{ mb: 2, fontSize: '2.5rem', fontWeight: 'bold', color: '#1976d2' }}>
                  {shipmentData.client}
                </Typography>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">
                  Shipment Details
                </Typography>
                <Chip 
                  label={shipmentData.status || 'Unknown'} 
                  color={shipmentData.status === 'delivered' ? 'success' : 
                         shipmentData.status === 'in_transit' ? 'primary' : 
                         shipmentData.status === 'voided' ? 'error' : 'default'} 
                />
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold">Shipment Code</Typography>
                  <Typography variant="body1" gutterBottom>{shipmentData.ship_code || 'N/A'}</Typography>
                  
                  <Typography variant="subtitle1" fontWeight="bold">Created Date</Typography>
                  <Typography variant="body1" gutterBottom>{formatDate(shipmentData.created_at)}</Typography>
                  
                  <Typography variant="subtitle1" fontWeight="bold">Service Type</Typography>
                  <Typography variant="body1" gutterBottom>{shipmentData.postage_type || shipmentData.service_type || 'N/A'}</Typography>
                  
                  <Typography variant="subtitle1" fontWeight="bold">Weight</Typography>
                  <Typography variant="body1" gutterBottom>
                    {shipmentData.weight ? `${shipmentData.weight} ${shipmentData.weight_unit || ''}` : 'N/A'}
                  </Typography>
                  
                  {/* Country of Origin */}
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>Country of Origin</Typography>
                  <Typography variant="body1" gutterBottom>
                    {getCountryOfOrigin(shipmentData)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold">Recipient</Typography>
                  <Typography variant="body1">{shipmentData.name || 'N/A'}</Typography>
                  <Typography variant="body1">
                    {[
                      shipmentData.address1,
                      shipmentData.address2,
                      `${shipmentData.city || ''}, ${shipmentData.province_code || ''} ${shipmentData.postal_code || ''}`,
                      shipmentData.country_code
                    ].filter(Boolean).join(', ')}
                  </Typography>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">Package Value</Typography>
                    <Typography variant="body1" gutterBottom>
                      {formatCurrency(shipmentData.value, shipmentData.currency)}
                    </Typography>
                  </Box>
                </Grid>
                
                {/* Items in shipment */}
                {shipmentData.items && shipmentData.items.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
                      Items ({shipmentData.items.length})
                    </Typography>
                    <Box sx={{ maxHeight: '200px', overflowY: 'auto', mt: 1 }}>
                      <table className="items-table">
                        <thead>
                          <tr>
                            <th>SKU</th>
                            <th>Description</th>
                            <th>Quantity</th>
                            <th>Value</th>
                            <th>Origin</th>
                            {hasChineseOrigin(shipmentData) && <th>Remove</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {shipmentData.items.map((item, index) => {
                            const origin = item.country_of_origin || item.origin;
                            const isChineseItem = origin === 'CN' || origin === 'China';
                            return (
                              <tr key={index} style={isChineseItem ? styles.chineseOriginItem : {}}>
                                <td>{item.sku || 'N/A'}</td>
                                <td>{item.description || 'N/A'}</td>
                                <td>{item.quantity || 'N/A'}</td>
                                <td>{formatCurrency(item.value, shipmentData.currency)}</td>
                                <td>
                                  {isChineseItem ? (
                                    <span style={styles.originWarning}>{origin}</span>
                                  ) : (
                                    origin || 'N/A'
                                  )}
                                </td>
                                {hasChineseOrigin(shipmentData) && (
                                  <td>
                                    {isChineseItem && (
                                      <input
                                        type="checkbox"
                                        checked={itemsToRemove.includes(index)}
                                        onChange={() => handleToggleItemRemoval(index)}
                                      />
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody> 
                      </table>
                    </Box>
                  </Grid>
                )}
                
                {/* Credits needed */}
                <Grid item xs={12}>
                  {hasChineseOrigin(shipmentData) && showChineseItemsWarning && (
                    <Box sx={{ mt: 2, mb: 2 }} style={styles.warningMessage}>
                      <Typography variant="body1" fontWeight="medium">
                        Warning: This shipment contains items from China. Due to current regulations, these items may cause issues.
                        {itemsToRemove.length > 0 ? (
                          <span> You've selected {itemsToRemove.length} item(s) to exclude from remanifesting.</span>
                        ) : (
                          <span> Select items to remove using the checkboxes, or remanifest with all items.</span>
                        )}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Credits Required for Remanifest: {creditsNeeded}
                    </Typography>
                  </Box>
                </Grid>
                
                {/* Action buttons */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                    {hasChineseOrigin(shipmentData) && !showChineseItemsWarning && (
                      <Button 
                        variant="contained" 
                        color="warning"
                        onClick={() => setShowChineseItemsWarning(true)}
                      >
                        Remanifest & Void Label
                      </Button>
                    )}
                    {(showChineseItemsWarning || !hasChineseOrigin(shipmentData)) && (
                      <Button 
                        variant="contained" 
                        color="primary"
                        onClick={handleRemanifest}
                        disabled={isRemanifesting || shipmentData.status === 'voided' || (credits && credits.available < creditsNeeded)}
                      >
                        {isRemanifesting ? (
                          <CircularProgress size={24} />
                        ) : hasChineseOrigin(shipmentData) ? (
                          `Remanifest ${itemsToRemove.length > 0 ? 'Without Selected Items' : 'All Items'}`
                        ) : (
                          'Remanifest & Void Label'
                        )}
                      </Button>
                    )}
                    
                    <Button 
                      variant="outlined"
                      onClick={handleClear}
                    >
                      Clear
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}
        </div>
    </div>
  );
};

export default Stallion;
